import { useSubscription } from '@apollo/client';
import { USER_EVENTS_SUBSCRIPTION } from '@/shared/subscriptions';
import type { SubscriptionResult } from '@/shared/types/generated';
import SentryService from '@/services/sentry/Sentry.service';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import { useAuth } from '@/services/auth/AuthProvider';
import useStore from '@/stores/useStore';
import { useGetUser } from './useGetUser';
import SoundNotificationService from '@/services/audio/SoundNotification.service';
import { IS_WEB } from '@/shared/constants/utility';
import { useVideoProvider } from './useVideoProvider';
import { RelativePathString, useRouter } from 'expo-router';
import { ROUTES } from '../constants/routes';

export const useUserEvents = () => {
  const { isAuthenticated } = useAuth();
  const { user, refetch: refetchUser } = useGetUser();
  const { endSession } = useVideoProvider();
  const router = useRouter();
  const setActiveSessionName = useStore((state) => state.setActiveSessionName);
  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const setOtherUserDisconnected = useStore(
    (state) => state.setOtherUserDisconnected,
  );
  const setLookbookPurchasedMessageId = useStore(
    (state) => state.setLookbookPurchasedMessageId,
  );
  const setVideoCallStarted = useInstantStylingStore(
    (state) => state.setVideoCallStarted,
  );
  const setMatchStatus = useInstantStylingStore(
    (state) => state.setMatchStatus,
  );
  const setIsInstantStylingActive = useInstantStylingStore(
    (state) => state.setIsInstantStylingActive,
  );

  const handleClientExitedCall = async (sessionName: string) => {
    // When client exits, stylist (host) should officially end the session
    await endSession(sessionName);

    router.replace(
      `${ROUTES.SESSIONS.path}?showPostCall=true` as RelativePathString,
    );
  };

  const { loading, error } = useSubscription<SubscriptionResult<'userEvents'>>(
    USER_EVENTS_SUBSCRIPTION,
    {
      skip: !isAuthenticated || !user,
      onData: ({ data }) => {
        const event = data.data?.userEvents;

        __DEV__ && console.log('Received user event:', event);

        if (!event) {
          SentryService.captureError('No user event data received');
          return;
        }

        switch (event.__typename) {
          case 'VideoCallStarted': {
            setVideoCallStarted(event);
            setActiveSessionName(event.sessionName);
            break;
          }
          case 'VideoCallDisconnected': {
            // Set flag to trigger proper session cleanup in ZoomVideoFeed components
            setOtherUserDisconnected(true);

            setSnackbarState({
              open: true,
              message: 'The other user has disconnected from the call',
              severity: 'info',
            });
            break;
          }
          case 'ClientExitedCall': {
            // Show notification to stylist that client has left
            const store = useStore.getState();
            const setCompletedSessionDuration =
              store.setCompletedSessionDuration;
            const setEstimatedSessionDuration =
              store.setEstimatedSessionDuration;
            const setIsPostCallModalVisible = store.setIsPostCallModalVisible;

            // Store duration from event if available
            if (event.durationSeconds != null) {
              setCompletedSessionDuration(event.durationSeconds);
            }
            if (event.estimatedDuration != null) {
              setEstimatedSessionDuration(event.estimatedDuration);
            }

            // Navigate and then set modal visible after ensuring duration is set
            // This ensures the duration is in the store before the modal renders
            // Pass sessionName from event to ensure we have the correct session
            handleClientExitedCall(event.sessionName);
            setIsPostCallModalVisible(true);
            break;
          }
          case 'InstantStylingMatchStatus': {
            setMatchStatus(event);

            // Play sound notification for stylists on web when they receive a NEW match
            // Only play for fresh matches, not cancellations, declines, or accepts
            const isNewMatch =
              event.requestId &&
              !event.stylistAccepted &&
              !event.clientCancelled &&
              !event.clientDeclined &&
              !event.stylistDeclined;

            if (IS_WEB && user?.stylist && isNewMatch) {
              SoundNotificationService.playMatchNotification().catch(
                (error) => {
                  SentryService.captureError(
                    'Failed to play match notification sound',
                    error,
                  );
                },
              );
            }

            break;
          }
          case 'InstantStylingTimeout': {
            refetchUser();
            setIsInstantStylingActive(false);

            if (event.message) {
              setSnackbarState({
                open: true,
                message: event.message,
                severity: 'info',
              });
            }
            break;
          }
          case 'LookbookPurchased': {
            setLookbookPurchasedMessageId(event.sendbirdMessageId);
            break;
          }
          default:
            SentryService.captureError(
              `Unknown user event type received: ${event.__typename}`,
            );
        }
      },
      onError: (error) => {
        SentryService.captureError('Error in user events subscription:', error);
      },
    },
  );

  function resetUserEvents() {
    setVideoCallStarted(null);
    setMatchStatus(null);
    setLookbookPurchasedMessageId(null);
    setOtherUserDisconnected(false);
  }

  return { loading, error, resetUserEvents };
};
